import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, X, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '' });
  const [error, setError] = useState('');

  const load = async () => {
    const [cats, items] = await Promise.all([
      api.getCategories(),
      api.getMenuItems()
    ]);

    const sortedCats = cats.sort(
      (a, b) => (a.position || 0) - (b.position || 0)
    );

    setCategories(sortedCats);
    setMenuItems(items);
    setLoading(false);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(categories);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    setCategories(items);

    try {
      await Promise.all(
        items.map((cat, index) =>
          api.updateCategory(cat.id, {
            name: cat.name,
            position: index
          })
        )
      );
    } catch (err) {
      console.error('Failed to update category order:', err);
      load();
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    if (!form.name) return;

    setError('');

    try {
      if (editing) {
        await api.updateCategory(editing, { name: form.name });
      } else {
        await api.createCategory({
          name: form.name,
          position: categories.length
        });
      }

      await load();
      setForm({ name: '' });
      setEditing(null);
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await api.deleteCategory(id);
    load();
  };

  if (loading)
    return (
      <div className="flex justify-center py-16">
        <div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  return (
    <div className="admin-view space-y-4">
      <AdminTopbarPortal><div className="admin-topbar-controls">
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            Menu Categories
          </h2>
          <p className="text-gray-500 text-sm">
            {categories.length} categories
          </p>
        </div>

        <button
          onClick={() => {
            setShowForm(true);
            setEditing(null);
            setForm({ name: '' });
          }}
          className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700"
        >
          <Plus className="w-4 h-4" /> Add Category
        </button>
      </div></AdminTopbarPortal>

      {showForm && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">
            {editing ? 'Edit Category' : 'New Category'}
          </h3>

          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Category name"
              value={form.name}
              onChange={(e) =>
                setForm({ name: e.target.value })
              }
              onKeyDown={(e) =>
                e.key === 'Enter' && handleSave()
              }
              className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500"
            />

            <button
              onClick={handleSave}
              className="bg-gray-900 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-amber-700"
            >
              {editing ? 'Update' : 'Add'}
            </button>

            <button
              onClick={() => {
                setShowForm(false);
                setEditing(null);
              }}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-xl hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <p className="text-red-500 text-xs mt-2">
              {error}
            </p>
          )}
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <p className="text-amber-800 text-sm font-medium">
          💡 Tip: Drag and drop categories to reorder them
        </p>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="categories" direction="vertical">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="admin-reorder-board flex flex-col w-full"
            >
              {categories.map((cat, i) => (
                <Draggable
                  key={cat.id}
                  draggableId={cat.id}
                  index={i}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        width: '100%',
                        minHeight: '60px'
                      }}
                      className={`bg-white rounded-xl border border-gray-200 p-4 mb-3 flex items-center justify-between ${
                        snapshot.isDragging
                          ? 'shadow-lg border-amber-300'
                          : ''
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          {...provided.dragHandleProps}
                          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-amber-600"
                        >
                          <GripVertical className="w-5 h-5" />
                        </div>

                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center text-amber-700 font-bold text-sm">
                          {i + 1}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm truncate">
                            {cat.name}
                          </p>
                          <p className="text-gray-400 text-xs">
                            {
                              menuItems.filter(
                                (item) =>
                                  item.categoryId === cat.id
                              ).length
                            }{' '}
                            items
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setForm({ name: cat.name });
                            setEditing(cat.id);
                            setShowForm(true);
                          }}
                          className="p-2 text-gray-400 hover:text-amber-600 rounded-lg hover:bg-amber-50"
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() =>
                            handleDelete(cat.id)
                          }
                          className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}
